import { useMemo } from "react";
import type { ErrorLike } from "@apollo/client";
import { useScopedActivityWhere } from "#app/widgets/dashboards/scope/use-scoped-activity-where";
import { useMemberLabelResolver } from "#app/lib/data";
import { useActivityGroupCounts } from "../shared/use-activity-group-counts";
import type { Omittable } from "@salestrak/ui";

export type MostActiveUserRow = {
  userId: string;
  /** The bar's category label — the chart reads one flat field per axis. */
  label: string;
  count: number;
};

/** What an actor id the directory cannot answer for is called. Such an id is a
 * person we cannot name — not a person named "Unknown" — and its event count is
 * exact either way, so the bar states the role. */
const PENDING_MEMBER_LABEL = "Member";

/**
 * Exact per-actor activity counts from the server aggregation (the `"actor"`
 * grouping token excludes system/null actors).
 */
// The result relaxes: `error` is Apollo's own `error` field, which is
// `undefined` while the query is healthy, and every widget body reads it as a
// truthiness check.
export function useMostActiveUsers(limit: number): Omittable<{
  rows: MostActiveUserRow[];
  loading: boolean;
  error?: ErrorLike;
}> {
  // Through the DIRECTORY, not `userMap`: the aggregation keys carry whoever was
  // active, and the bootstrap page answers for 500 members in name order — so
  // which actors could be named came down to the alphabet.
  const resolveMemberLabel = useMemberLabelResolver();
  const { where } = useScopedActivityWhere(undefined, {});
  const { counts, ready, error } = useActivityGroupCounts({ groupBy: "actor", where });

  const rows = useMemo(
    () =>
      Object.entries(counts)
        .map(([userId, count]) => ({
          userId,
          label: resolveMemberLabel(userId) ?? PENDING_MEMBER_LABEL,
          count,
        }))
        .sort((a, b) => b.count - a.count)
        .slice(0, limit),
    [counts, limit, resolveMemberLabel],
  );

  return { rows, loading: !ready, error };
}
